/**
 * BuilderScene - Organism construction gameplay
 * Players build cells, tissues, and organs by placing components
 */

import Phaser from 'phaser';
import { CellAnimationSystem } from '@/procedural/graphics';
import { getLevel, getChapterForLevel } from '@/data';
import { markLevelComplete } from './MenuScene';
import type { Level, LevelObjective } from '@/types';

export interface BuilderSceneConfig {
  levelId: string;
}

interface PlacedComponent {
  id: string;
  type: ComponentType;
  x: number;
  y: number;
  container: Phaser.GameObjects.Container;
}

interface PlacedCell {
  id: string;
  cellType: CellBuildType;
  x: number;
  y: number;
  container: Phaser.GameObjects.Container;
  components: PlacedComponent[];
}

type ComponentType = 'membrane' | 'cytoplasm' | 'nucleus' | 'mitochondria' | 'ribosome' | 'chloroplast' | 'vacuole' | 'er';
type CellBuildType = 'animal' | 'plant' | 'bacteria';
type TissueType = 'epithelial' | 'muscle' | 'connective' | 'nervous';

interface Blueprint {
  type: 'cell' | 'tissue' | 'organ' | 'organism';
  requiredComponents?: ComponentType[];
  requiredCellCount?: number;
  requiredCellTypes?: CellBuildType[];
  requiredTissueTypes?: TissueType[];
  arrangementPattern?: 'grid' | 'hexagonal' | 'layered';
  lifeFunctionsToTest?: string[];
}

interface ObjectiveProgress {
  objective: LevelObjective;
  current: number;
  target: number;
  complete: boolean;
}

const COMPONENT_COLORS: Record<ComponentType, number> = {
  membrane: 0x90cdf4,
  cytoplasm: 0xe2e8f0,
  nucleus: 0x805ad5,
  mitochondria: 0xed8936,
  ribosome: 0xd53f8c,
  chloroplast: 0x48bb78,
  vacuole: 0x63b3ed,
  er: 0x68d391,
};

const COMPONENT_LABELS: Record<ComponentType, string> = {
  membrane: 'Membrane',
  cytoplasm: 'Cytoplasm',
  nucleus: 'Nucleus',
  mitochondria: 'Mitochondria',
  ribosome: 'Ribosome',
  chloroplast: 'Chloroplast',
  vacuole: 'Vacuole',
  er: 'ER',
};

// Cell type component requirements - used for validation
const _CELL_REQUIRED_COMPONENTS: Record<CellBuildType, ComponentType[]> = {
  animal: ['membrane', 'cytoplasm', 'nucleus', 'mitochondria', 'ribosome'],
  plant: ['membrane', 'cytoplasm', 'nucleus', 'mitochondria', 'chloroplast', 'vacuole'],
  bacteria: ['membrane', 'cytoplasm', 'ribosome'],
};
void _CELL_REQUIRED_COMPONENTS; // Reserved for future validation

/**
 * BuilderScene handles organism construction gameplay
 * - Component/cell palette selection
 * - Canvas placement
 * - Blueprint matching
 * - Life function testing
 */
export class BuilderScene extends Phaser.Scene {
  private levelId!: string;
  private level!: Level;

  /** Systems */
  private animationSystem!: CellAnimationSystem;

  /** Game objects */
  private canvasContainer!: Phaser.GameObjects.Container;
  private paletteContainer!: Phaser.GameObjects.Container;
  private blueprintContainer!: Phaser.GameObjects.Container;
  private objectivePanel!: Phaser.GameObjects.Container;

  /** State */
  private selectedComponent: ComponentType | null = null;
  private selectedCellType: CellBuildType | null = null;
  private placedComponents: PlacedComponent[] = [];
  private placedCells: PlacedCell[] = [];
  private objectives: ObjectiveProgress[] = [];
  private currentBlueprint!: Blueprint;
  private buildMode: 'component' | 'cell' = 'component';

  /** UI elements */
  private ghostPreview?: Phaser.GameObjects.Container;
  private completionOverlay?: Phaser.GameObjects.Container;
  private testResultsPanel?: Phaser.GameObjects.Container;

  /** Canvas bounds */
  private canvasBounds!: Phaser.Geom.Rectangle;

  constructor() {
    super({ key: 'BuilderScene' });
  }

  init(data: BuilderSceneConfig): void {
    this.levelId = data.levelId;

    // Reset state
    this.placedComponents = [];
    this.placedCells = [];
    this.objectives = [];
    this.selectedComponent = null;
    this.selectedCellType = null;
  }

  create(): void {
    // Load level data
    const level = getLevel(this.levelId);
    if (!level) {
      console.error(`Level not found: ${this.levelId}`);
      this.scene.start('MenuScene');
      return;
    }
    this.level = level;

    // Initialize systems
    this.animationSystem = new CellAnimationSystem(this);

    // Determine build mode based on level
    this.buildMode = this.determineBuildMode();

    // Create blueprint based on level
    this.currentBlueprint = this.createBlueprintFromLevel();

    // Create background
    this.createBackground();

    // Create main layout
    this.createLayout();

    // Initialize objectives
    this.initializeObjectives();

    // Setup input
    this.setupInput();

    // Emit level start event
    this.events.emit('level:start', { levelId: this.levelId, level: this.level });
  }

  override update(_time: number, delta: number): void {
    this.animationSystem.update(delta);

    // Update ghost preview position
    if (this.ghostPreview && this.ghostPreview.visible) {
      const pointer = this.input.activePointer;
      if (this.canvasBounds.contains(pointer.x, pointer.y)) {
        this.ghostPreview.setPosition(pointer.x, pointer.y);
        this.ghostPreview.setAlpha(0.6);
      } else {
        this.ghostPreview.setAlpha(0.3);
      }
    }
  }

  /**
   * Determine build mode from level challenge type
   */
  private determineBuildMode(): 'component' | 'cell' {
    if (this.level.challenge === 'construct_simple_cell') {
      return 'component';
    }
    return 'cell';
  }

  /**
   * Create blueprint from level definition
   */
  private createBlueprintFromLevel(): Blueprint {
    const challenge = this.level.challenge || 'construct_simple_cell';

    switch (challenge) {
      case 'construct_simple_cell':
        return {
          type: 'cell',
          requiredComponents: (this.level.components as ComponentType[]) || ['membrane', 'cytoplasm', 'nucleus', 'mitochondria', 'ribosome'],
          lifeFunctionsToTest: ['metabolism', 'growth'],
        };

      case 'arrange_cells_into_tissue':
        return {
          type: 'tissue',
          requiredCellCount: 6,
          requiredCellTypes: ['animal'],
          arrangementPattern: 'hexagonal',
          lifeFunctionsToTest: ['metabolism', 'response'],
        };

      case 'combine_tissues_into_organ':
        return {
          type: 'organ',
          requiredCellCount: 12,
          requiredTissueTypes: ['epithelial', 'muscle'],
          lifeFunctionsToTest: ['metabolism', 'response', 'homeostasis'],
        };

      case 'build_functional_organism':
        return {
          type: 'organism',
          requiredCellCount: 20,
          requiredTissueTypes: ['epithelial', 'muscle', 'connective'],
          lifeFunctionsToTest: ['metabolism', 'growth', 'response', 'homeostasis', 'reproduction', 'waste_removal', 'gas_exchange'],
        };

      default:
        return {
          type: 'cell',
          requiredComponents: ['membrane', 'cytoplasm', 'nucleus'],
        };
    }
  }

  /**
   * Create deep bioluminescent background
   */
  private createBackground(): void {
    const { width, height } = this.cameras.main;

    // Deep ocean base
    const bg = this.add.graphics();
    bg.fillStyle(0x0a1628, 1);
    bg.fillRect(0, 0, width, height);

    // Radial gradient overlay
    const gradient = this.add.graphics();
    const centerX = width / 2;
    const centerY = height / 2;

    for (let i = 20; i >= 0; i--) {
      const ratio = i / 20;
      const radius = Math.max(width, height) * 0.7 * ratio;
      const alpha = 0.03 * (1 - ratio);
      gradient.fillStyle(0x1e3a5f, alpha);
      gradient.fillCircle(centerX, centerY, radius);
    }

    // Subtle floating particles
    for (let i = 0; i < 30; i++) {
      const x = Math.random() * width;
      const y = Math.random() * height;
      const size = 1 + Math.random() * 2;
      const alpha = 0.1 + Math.random() * 0.2;

      const particle = this.add.circle(x, y, size, 0x4299e1, alpha);

      // Gentle floating animation
      this.tweens.add({
        targets: particle,
        y: y + (Math.random() - 0.5) * 50,
        alpha: alpha * 0.5,
        duration: 3000 + Math.random() * 4000,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }
  }

  /**
   * Create main UI layout
   */
  private createLayout(): void {
    // Header with level info
    this.createHeader();

    // Left panel: Palette
    this.createPalette();

    // Center: Canvas
    this.createCanvas();

    // Right panel: Blueprint & Objectives
    this.createRightPanel();

    // Bottom: Action buttons
    this.createActionButtons();

    // Back button
    this.createBackButton();
  }

  /**
   * Create header with level title
   */
  private createHeader(): void {
    const { width } = this.cameras.main;

    // Level title
    const chapter = getChapterForLevel(this.levelId);
    const chapterTitle = chapter ? chapter.title : 'Building Life';

    this.add.text(width / 2, 30, chapterTitle, {
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: '18px',
      color: '#63B3ED',
    }).setOrigin(0.5);

    this.add.text(width / 2, 55, this.level.name, {
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: '28px',
      fontStyle: 'bold',
      color: '#E2E8F0',
    }).setOrigin(0.5);
  }

  /**
   * Create component/cell palette
   */
  private createPalette(): void {
    const { height } = this.cameras.main;

    this.paletteContainer = this.add.container(20, 100);

    // Palette background
    const paletteBg = this.add.graphics();
    paletteBg.fillStyle(0x1a365d, 0.9);
    paletteBg.fillRoundedRect(0, 0, 140, height - 200, 12);
    paletteBg.lineStyle(2, 0x2d5a87, 1);
    paletteBg.strokeRoundedRect(0, 0, 140, height - 200, 12);
    this.paletteContainer.add(paletteBg);

    // Palette title
    const title = this.add.text(70, 20, this.buildMode === 'component' ? 'PARTS' : 'CELLS', {
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: '14px',
      fontStyle: 'bold',
      color: '#90CDF4',
    }).setOrigin(0.5);
    this.paletteContainer.add(title);

    // Create palette items
    if (this.buildMode === 'component') {
      this.createComponentPalette();
    } else {
      this.createCellPalette();
    }
  }

  /**
   * Create component palette items
   */
  private createComponentPalette(): void {
    const components: ComponentType[] = (this.level.components as ComponentType[]) ||
      ['membrane', 'cytoplasm', 'nucleus', 'mitochondria', 'ribosome'];

    components.forEach((comp, index) => {
      const itemY = 50 + index * 70;
      const item = this.createPaletteItem(comp, 70, itemY);
      this.paletteContainer.add(item);
    });
  }

  /**
   * Create cell type palette items
   */
  private createCellPalette(): void {
    const cellTypes: CellBuildType[] = ['animal', 'plant', 'bacteria'];

    cellTypes.forEach((cellType, index) => {
      const itemY = 50 + index * 90;
      const item = this.createCellPaletteItem(cellType, 70, itemY);
      this.paletteContainer.add(item);
    });
  }

  /**
   * Create a single palette item for components
   */
  private createPaletteItem(comp: ComponentType, x: number, y: number): Phaser.GameObjects.Container {
    const container = this.add.container(x, y);

    // Item background
    const bg = this.add.graphics();
    bg.fillStyle(0x2d3748, 0.8);
    bg.fillRoundedRect(-50, -25, 100, 50, 8);
    container.add(bg);

    // Component preview
    const preview = this.createComponentPreview(comp, 0, -5, 20);
    container.add(preview);

    // Label
    const label = this.add.text(0, 18, COMPONENT_LABELS[comp], {
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: '11px',
      color: '#A0AEC0',
    }).setOrigin(0.5);
    container.add(label);

    // Make interactive
    const hitArea = this.add.rectangle(0, 0, 100, 50, 0x000000, 0);
    hitArea.setInteractive({ useHandCursor: true });
    container.add(hitArea);

    hitArea.on('pointerover', () => {
      bg.clear();
      bg.fillStyle(0x4a5568, 0.9);
      bg.fillRoundedRect(-50, -25, 100, 50, 8);
      bg.lineStyle(2, COMPONENT_COLORS[comp], 0.8);
      bg.strokeRoundedRect(-50, -25, 100, 50, 8);
    });

    hitArea.on('pointerout', () => {
      if (this.selectedComponent !== comp) {
        bg.clear();
        bg.fillStyle(0x2d3748, 0.8);
        bg.fillRoundedRect(-50, -25, 100, 50, 8);
      }
    });

    hitArea.on('pointerdown', () => {
      this.selectComponent(comp);
    });

    return container;
  }

  /**
   * Create a single palette item for cells
   */
  private createCellPaletteItem(cellType: CellBuildType, x: number, y: number): Phaser.GameObjects.Container {
    const container = this.add.container(x, y);

    // Item background
    const bg = this.add.graphics();
    bg.fillStyle(0x2d3748, 0.8);
    bg.fillRoundedRect(-55, -35, 110, 70, 8);
    container.add(bg);

    // Cell preview
    const preview = this.createCellPreview(cellType, 0, -5);
    container.add(preview);

    // Label
    const labels: Record<CellBuildType, string> = {
      animal: 'Animal Cell',
      plant: 'Plant Cell',
      bacteria: 'Bacterium',
    };
    const label = this.add.text(0, 25, labels[cellType], {
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: '11px',
      color: '#A0AEC0',
    }).setOrigin(0.5);
    container.add(label);

    // Make interactive
    const hitArea = this.add.rectangle(0, 0, 110, 70, 0x000000, 0);
    hitArea.setInteractive({ useHandCursor: true });
    container.add(hitArea);

    hitArea.on('pointerover', () => {
      bg.clear();
      bg.fillStyle(0x4a5568, 0.9);
      bg.fillRoundedRect(-55, -35, 110, 70, 8);
      bg.lineStyle(2, 0x4299e1, 0.8);
      bg.strokeRoundedRect(-55, -35, 110, 70, 8);
    });

    hitArea.on('pointerout', () => {
      if (this.selectedCellType !== cellType) {
        bg.clear();
        bg.fillStyle(0x2d3748, 0.8);
        bg.fillRoundedRect(-55, -35, 110, 70, 8);
      }
    });

    hitArea.on('pointerdown', () => {
      this.selectCellType(cellType);
    });

    return container;
  }

  /**
   * Create component visual preview
   */
  private createComponentPreview(comp: ComponentType, x: number, y: number, size: number = 30): Phaser.GameObjects.Graphics {
    const g = this.add.graphics();
    const color = COMPONENT_COLORS[comp];

    switch (comp) {
      case 'membrane':
        g.lineStyle(3, color, 0.9);
        g.strokeCircle(x, y, size);
        break;

      case 'cytoplasm':
        g.fillStyle(color, 0.5);
        g.fillCircle(x, y, size);
        break;

      case 'nucleus':
        g.fillStyle(color, 0.9);
        g.fillCircle(x, y, size * 0.6);
        g.lineStyle(2, 0x9f7aea, 1);
        g.strokeCircle(x, y, size * 0.6);
        break;

      case 'mitochondria':
        g.fillStyle(color, 0.9);
        g.fillEllipse(x, y, size * 1.2, size * 0.6);
        // Cristae lines
        g.lineStyle(1, 0xc05621, 0.7);
        for (let i = -2; i <= 2; i++) {
          g.lineBetween(x + i * 4, y - size * 0.2, x + i * 4, y + size * 0.2);
        }
        break;

      case 'ribosome':
        g.fillStyle(color, 0.9);
        g.fillCircle(x - 4, y, size * 0.3);
        g.fillCircle(x + 4, y, size * 0.3);
        g.fillCircle(x, y - 5, size * 0.25);
        break;

      case 'chloroplast':
        g.fillStyle(color, 0.9);
        g.fillEllipse(x, y, size * 1.4, size * 0.7);
        // Thylakoid stacks
        g.lineStyle(2, 0x276749, 0.8);
        for (let i = -2; i <= 2; i++) {
          const stackY = y + i * 4;
          g.lineBetween(x - size * 0.4, stackY, x + size * 0.4, stackY);
        }
        break;

      case 'vacuole':
        g.fillStyle(color, 0.6);
        g.fillCircle(x, y, size * 0.9);
        g.lineStyle(2, 0x3182ce, 0.8);
        g.strokeCircle(x, y, size * 0.9);
        break;

      case 'er':
        g.lineStyle(2, color, 0.9);
        // Wavy ER network
        const points: number[] = [];
        for (let i = 0; i <= 6; i++) {
          const px = x - size + (i * size * 2) / 6;
          const py = y + Math.sin(i * Math.PI / 2) * size * 0.3;
          points.push(px, py);
        }
        g.strokePoints(points, false);
        break;
    }

    return g;
  }

  /**
   * Create cell visual preview
   */
  private createCellPreview(cellType: CellBuildType, x: number, y: number): Phaser.GameObjects.Container {
    const container = this.add.container(x, y);
    const g = this.add.graphics();
    container.add(g);

    const size = 25;

    // Membrane
    g.lineStyle(2, 0x90cdf4, 0.9);
    g.strokeCircle(0, 0, size);

    // Cytoplasm
    g.fillStyle(0xe2e8f0, 0.3);
    g.fillCircle(0, 0, size - 2);

    // Cell-specific features
    if (cellType === 'animal' || cellType === 'plant') {
      // Nucleus
      g.fillStyle(0x805ad5, 0.8);
      g.fillCircle(0, 0, size * 0.3);
    }

    if (cellType === 'plant') {
      // Chloroplasts
      g.fillStyle(0x48bb78, 0.8);
      g.fillEllipse(-size * 0.5, -size * 0.3, 8, 4);
      g.fillEllipse(size * 0.4, size * 0.4, 8, 4);
    }

    // Mitochondria
    g.fillStyle(0xed8936, 0.8);
    g.fillEllipse(size * 0.4, -size * 0.3, 6, 3);

    return container;
  }

  /**
   * Create the building canvas
   */
  private createCanvas(): void {
    const { width, height } = this.cameras.main;

    const canvasX = 180;
    const canvasY = 90;
    const canvasWidth = width - 380;
    const canvasHeight = height - 180;

    this.canvasBounds = new Phaser.Geom.Rectangle(canvasX, canvasY, canvasWidth, canvasHeight);

    this.canvasContainer = this.add.container(canvasX, canvasY);

    // Canvas background
    const canvasBg = this.add.graphics();
    canvasBg.fillStyle(0x0d2137, 0.95);
    canvasBg.fillRoundedRect(0, 0, canvasWidth, canvasHeight, 16);
    canvasBg.lineStyle(3, 0x2d5a87, 1);
    canvasBg.strokeRoundedRect(0, 0, canvasWidth, canvasHeight, 16);
    this.canvasContainer.add(canvasBg);

    // Grid overlay for cell placement
    if (this.buildMode === 'cell') {
      this.createPlacementGrid(canvasWidth, canvasHeight);
    }

    // Canvas label
    const canvasLabel = this.add.text(canvasWidth / 2, -15, 'BUILDING AREA', {
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: '12px',
      color: '#4A5568',
    }).setOrigin(0.5);
    this.canvasContainer.add(canvasLabel);

    // Make canvas interactive for placement
    const canvasHitArea = this.add.rectangle(canvasWidth / 2, canvasHeight / 2, canvasWidth, canvasHeight, 0x000000, 0);
    canvasHitArea.setInteractive();
    this.canvasContainer.add(canvasHitArea);

    canvasHitArea.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      this.handleCanvasClick(pointer.x - canvasX, pointer.y - canvasY);
    });
  }

  /**
   * Create placement grid for cell mode
   */
  private createPlacementGrid(width: number, height: number): void {
    const grid = this.add.graphics();
    grid.lineStyle(1, 0x2d5a87, 0.3);

    const cellSize = 60;
    const offsetX = (width % cellSize) / 2;
    const offsetY = (height % cellSize) / 2;

    // Vertical lines
    for (let x = offsetX; x <= width; x += cellSize) {
      grid.lineBetween(x, 0, x, height);
    }

    // Horizontal lines
    for (let y = offsetY; y <= height; y += cellSize) {
      grid.lineBetween(0, y, width, y);
    }

    this.canvasContainer.add(grid);
  }

  /**
   * Create right panel with blueprint and objectives
   */
  private createRightPanel(): void {
    const { width } = this.cameras.main;

    const panelX = width - 180;
    const panelY = 100;

    // Blueprint display
    this.createBlueprintDisplay(panelX, panelY);

    // Objectives panel
    this.createObjectivePanel(panelX, panelY + 200);
  }

  /**
   * Create blueprint display
   */
  private createBlueprintDisplay(x: number, y: number): void {
    this.blueprintContainer = this.add.container(x, y);

    // Background
    const bg = this.add.graphics();
    bg.fillStyle(0x1a365d, 0.9);
    bg.fillRoundedRect(0, 0, 160, 180, 12);
    bg.lineStyle(2, 0x2d5a87, 1);
    bg.strokeRoundedRect(0, 0, 160, 180, 12);
    this.blueprintContainer.add(bg);

    // Title
    const title = this.add.text(80, 15, 'BLUEPRINT', {
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: '12px',
      fontStyle: 'bold',
      color: '#90CDF4',
    }).setOrigin(0.5);
    this.blueprintContainer.add(title);

    // Blueprint preview
    this.createBlueprintPreview();

    // Requirements list
    this.createRequirementsList();
  }

  /**
   * Create blueprint visual preview
   */
  private createBlueprintPreview(): void {
    const preview = this.add.graphics();

    if (this.currentBlueprint.type === 'cell') {
      // Show target cell outline
      preview.lineStyle(2, 0x4299e1, 0.6);
      preview.strokeCircle(80, 80, 40);
      preview.setAlpha(0.7);

      // Show component hints
      if (this.currentBlueprint.requiredComponents) {
        this.currentBlueprint.requiredComponents.forEach((comp, i) => {
          const angle = (i / this.currentBlueprint.requiredComponents!.length) * Math.PI * 2;
          const cx = 80 + Math.cos(angle) * 20;
          const cy = 80 + Math.sin(angle) * 20;
          preview.fillStyle(COMPONENT_COLORS[comp], 0.4);
          preview.fillCircle(cx, cy, 6);
        });
      }
    } else {
      // Show tissue/organ outline
      preview.lineStyle(2, 0x4299e1, 0.4);
      for (let i = 0; i < (this.currentBlueprint.requiredCellCount || 6); i++) {
        const row = Math.floor(i / 3);
        const col = i % 3;
        const cx = 40 + col * 40;
        const cy = 50 + row * 40;
        preview.strokeCircle(cx, cy, 15);
      }
    }

    this.blueprintContainer.add(preview);
  }

  /**
   * Create requirements checklist
   */
  private createRequirementsList(): void {
    const startY = 130;

    if (this.currentBlueprint.type === 'cell' && this.currentBlueprint.requiredComponents) {
      const text = `Need: ${this.currentBlueprint.requiredComponents.length} parts`;
      const reqText = this.add.text(80, startY, text, {
        fontFamily: 'system-ui, -apple-system, sans-serif',
        fontSize: '11px',
        color: '#A0AEC0',
      }).setOrigin(0.5);
      this.blueprintContainer.add(reqText);
    } else if (this.currentBlueprint.requiredCellCount) {
      const text = `Need: ${this.currentBlueprint.requiredCellCount} cells`;
      const reqText = this.add.text(80, startY, text, {
        fontFamily: 'system-ui, -apple-system, sans-serif',
        fontSize: '11px',
        color: '#A0AEC0',
      }).setOrigin(0.5);
      this.blueprintContainer.add(reqText);
    }

    // Status indicator
    const statusText = this.add.text(80, startY + 25, 'In Progress...', {
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: '11px',
      color: '#ECC94B',
    }).setOrigin(0.5);
    this.blueprintContainer.add(statusText);
  }

  /**
   * Create objectives panel
   */
  private createObjectivePanel(x: number, y: number): void {
    this.objectivePanel = this.add.container(x, y);

    // Background
    const bg = this.add.graphics();
    bg.fillStyle(0x1a365d, 0.9);
    bg.fillRoundedRect(0, 0, 160, 200, 12);
    bg.lineStyle(2, 0x2d5a87, 1);
    bg.strokeRoundedRect(0, 0, 160, 200, 12);
    this.objectivePanel.add(bg);

    // Title
    const title = this.add.text(80, 15, 'OBJECTIVES', {
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: '12px',
      fontStyle: 'bold',
      color: '#90CDF4',
    }).setOrigin(0.5);
    this.objectivePanel.add(title);

    // Objectives will be populated in initializeObjectives
  }

  /**
   * Create action buttons
   */
  private createActionButtons(): void {
    const { width, height } = this.cameras.main;
    const buttonY = height - 50;

    // Test button
    this.createButton(width / 2 - 80, buttonY, 'TEST', 0x48bb78, () => {
      this.testConstruction();
    });

    // Clear button
    this.createButton(width / 2 + 80, buttonY, 'CLEAR', 0xe53e3e, () => {
      this.clearCanvas();
    });
  }

  /**
   * Create a styled button
   */
  private createButton(x: number, y: number, label: string, color: number, onClick: () => void): Phaser.GameObjects.Container {
    const container = this.add.container(x, y);

    const bg = this.add.graphics();
    bg.fillStyle(color, 0.9);
    bg.fillRoundedRect(-60, -20, 120, 40, 8);
    container.add(bg);

    const text = this.add.text(0, 0, label, {
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: '16px',
      fontStyle: 'bold',
      color: '#FFFFFF',
    }).setOrigin(0.5);
    container.add(text);

    const hitArea = this.add.rectangle(0, 0, 120, 40, 0x000000, 0);
    hitArea.setInteractive({ useHandCursor: true });
    container.add(hitArea);

    hitArea.on('pointerover', () => {
      container.setScale(1.05);
    });

    hitArea.on('pointerout', () => {
      container.setScale(1);
    });

    hitArea.on('pointerdown', onClick);

    return container;
  }

  /**
   * Create back button
   */
  private createBackButton(): void {
    const backBtn = this.add.container(50, 30);

    const bg = this.add.graphics();
    bg.fillStyle(0x2d3748, 0.8);
    bg.fillRoundedRect(-30, -15, 60, 30, 6);
    backBtn.add(bg);

    const text = this.add.text(0, 0, '< Back', {
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: '14px',
      color: '#A0AEC0',
    }).setOrigin(0.5);
    backBtn.add(text);

    const hitArea = this.add.rectangle(0, 0, 60, 30, 0x000000, 0);
    hitArea.setInteractive({ useHandCursor: true });
    backBtn.add(hitArea);

    hitArea.on('pointerover', () => {
      bg.clear();
      bg.fillStyle(0x4a5568, 0.9);
      bg.fillRoundedRect(-30, -15, 60, 30, 6);
    });

    hitArea.on('pointerout', () => {
      bg.clear();
      bg.fillStyle(0x2d3748, 0.8);
      bg.fillRoundedRect(-30, -15, 60, 30, 6);
    });

    hitArea.on('pointerdown', () => {
      this.scene.start('MenuScene');
    });
  }

  /**
   * Initialize objectives from level
   */
  private initializeObjectives(): void {
    this.objectives = this.level.objectives.map(obj => ({
      objective: obj,
      current: 0,
      target: obj.count || 1,
      complete: false,
    }));

    this.updateObjectiveDisplay();
  }

  /**
   * Update objectives display
   */
  private updateObjectiveDisplay(): void {
    // Remove existing objective items
    this.objectivePanel.list.forEach(child => {
      if (child instanceof Phaser.GameObjects.Text && child.y > 30) {
        child.destroy();
      }
    });

    // Add objective items
    this.objectives.forEach((obj, index) => {
      const y = 45 + index * 35;
      const checkmark = obj.complete ? '[OK]' : '[ ]';
      const color = obj.complete ? '#48BB78' : '#A0AEC0';

      const objText = this.add.text(15, y, `${checkmark} ${this.getObjectiveLabel(obj.objective)}`, {
        fontFamily: 'system-ui, -apple-system, sans-serif',
        fontSize: '11px',
        color,
        wordWrap: { width: 130 },
      });
      this.objectivePanel.add(objText);

      // Progress bar if applicable
      if (obj.target > 1) {
        const progressBg = this.add.graphics();
        progressBg.fillStyle(0x2d3748, 1);
        progressBg.fillRoundedRect(15, y + 18, 130, 6, 3);
        this.objectivePanel.add(progressBg);

        const progress = this.add.graphics();
        const progressWidth = Math.min(130, (obj.current / obj.target) * 130);
        progress.fillStyle(obj.complete ? 0x48bb78 : 0x4299e1, 1);
        progress.fillRoundedRect(15, y + 18, progressWidth, 6, 3);
        this.objectivePanel.add(progress);
      }
    });
  }

  /**
   * Get human-readable objective label
   */
  private getObjectiveLabel(obj: LevelObjective): string {
    switch (obj.type) {
      case 'construct_cell':
        return 'Build a complete cell';
      case 'cell_functions_test':
        return 'Pass function test';
      case 'arrange_cells_into_tissue':
        return `Place ${obj.count || 6} cells`;
      case 'tissue_pattern_match':
        return 'Match tissue pattern';
      case 'combine_tissues':
        return `Combine ${obj.count || 3} tissues`;
      case 'organ_functions_test':
        return 'Pass organ test';
      case 'build_functional_organism':
        return 'Build organism';
      case 'all_life_functions_present':
        return 'All 7 life functions';
      default:
        return obj.type.replace(/_/g, ' ');
    }
  }

  /**
   * Setup input handlers
   */
  private setupInput(): void {
    // Create ghost preview for selected item
    this.ghostPreview = this.add.container(0, 0);
    this.ghostPreview.setVisible(false);
    this.ghostPreview.setAlpha(0.6);
    this.ghostPreview.setDepth(100);

    // ESC to deselect
    this.input.keyboard?.on('keydown-ESC', () => {
      this.deselectAll();
    });
  }

  /**
   * Select a component from palette
   */
  private selectComponent(comp: ComponentType): void {
    this.selectedComponent = comp;
    this.selectedCellType = null;

    // Update ghost preview
    this.ghostPreview?.removeAll(true);
    const preview = this.createComponentPreview(comp, 0, 0, 25);
    this.ghostPreview?.add(preview);
    this.ghostPreview?.setVisible(true);
  }

  /**
   * Select a cell type from palette
   */
  private selectCellType(cellType: CellBuildType): void {
    this.selectedCellType = cellType;
    this.selectedComponent = null;

    // Update ghost preview
    this.ghostPreview?.removeAll(true);
    const preview = this.createCellPreview(cellType, 0, 0);
    this.ghostPreview?.add(preview);
    this.ghostPreview?.setVisible(true);
  }

  /**
   * Deselect all
   */
  private deselectAll(): void {
    this.selectedComponent = null;
    this.selectedCellType = null;
    this.ghostPreview?.setVisible(false);
  }

  /**
   * Handle canvas click for placement
   */
  private handleCanvasClick(localX: number, localY: number): void {
    if (this.buildMode === 'component' && this.selectedComponent) {
      this.placeComponent(localX, localY);
    } else if (this.buildMode === 'cell' && this.selectedCellType) {
      this.placeCell(localX, localY);
    }
  }

  /**
   * Place a component on the canvas
   */
  private placeComponent(x: number, y: number): void {
    if (!this.selectedComponent) return;

    const id = `comp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // Create visual
    const container = this.add.container(x, y);
    const visual = this.createComponentPreview(this.selectedComponent, 0, 0, 35);
    container.add(visual);

    // Add to canvas
    this.canvasContainer.add(container);

    // Store placement
    const placement: PlacedComponent = {
      id,
      type: this.selectedComponent,
      x,
      y,
      container,
    };
    this.placedComponents.push(placement);

    // Make removable on right-click
    const hitArea = this.add.circle(0, 0, 35, 0x000000, 0);
    hitArea.setInteractive({ useHandCursor: true });
    container.add(hitArea);

    hitArea.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (pointer.rightButtonDown()) {
        this.removeComponent(placement);
      }
    });

    // Placement animation
    container.setScale(0);
    this.tweens.add({
      targets: container,
      scale: 1,
      duration: 200,
      ease: 'Back.easeOut',
    });

    // Check objectives
    this.checkBuildProgress();
  }

  /**
   * Place a cell on the canvas
   */
  private placeCell(x: number, y: number): void {
    if (!this.selectedCellType) return;

    // Snap to grid
    const gridSize = 60;
    const canvasWidth = this.canvasBounds.width;
    const canvasHeight = this.canvasBounds.height;
    const offsetX = (canvasWidth % gridSize) / 2;
    const offsetY = (canvasHeight % gridSize) / 2;

    const snappedX = Math.round((x - offsetX) / gridSize) * gridSize + offsetX;
    const snappedY = Math.round((y - offsetY) / gridSize) * gridSize + offsetY;

    // Check if position already occupied
    const occupied = this.placedCells.some(cell =>
      Math.abs(cell.x - snappedX) < 30 && Math.abs(cell.y - snappedY) < 30
    );
    if (occupied) return;

    const id = `cell_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // Create visual
    const container = this.add.container(snappedX, snappedY);
    const visual = this.createCellPreview(this.selectedCellType, 0, 0);
    visual.setScale(1.5);
    container.add(visual);

    // Add to canvas
    this.canvasContainer.add(container);

    // Store placement
    const placement: PlacedCell = {
      id,
      cellType: this.selectedCellType,
      x: snappedX,
      y: snappedY,
      container,
      components: [],
    };
    this.placedCells.push(placement);

    // Make removable on right-click
    const hitArea = this.add.circle(0, 0, 30, 0x000000, 0);
    hitArea.setInteractive({ useHandCursor: true });
    container.add(hitArea);

    hitArea.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (pointer.rightButtonDown()) {
        this.removeCell(placement);
      }
    });

    // Placement animation
    container.setScale(0);
    this.tweens.add({
      targets: container,
      scale: 1,
      duration: 200,
      ease: 'Back.easeOut',
    });

    // Check objectives
    this.checkBuildProgress();
  }

  /**
   * Remove a placed component
   */
  private removeComponent(placement: PlacedComponent): void {
    this.tweens.add({
      targets: placement.container,
      scale: 0,
      alpha: 0,
      duration: 150,
      ease: 'Back.easeIn',
      onComplete: () => {
        placement.container.destroy();
        this.placedComponents = this.placedComponents.filter(p => p.id !== placement.id);
        this.checkBuildProgress();
      },
    });
  }

  /**
   * Remove a placed cell
   */
  private removeCell(placement: PlacedCell): void {
    this.tweens.add({
      targets: placement.container,
      scale: 0,
      alpha: 0,
      duration: 150,
      ease: 'Back.easeIn',
      onComplete: () => {
        placement.container.destroy();
        this.placedCells = this.placedCells.filter(p => p.id !== placement.id);
        this.checkBuildProgress();
      },
    });
  }

  /**
   * Clear all placed items
   */
  private clearCanvas(): void {
    [...this.placedComponents].forEach(p => this.removeComponent(p));
    [...this.placedCells].forEach(p => this.removeCell(p));
  }

  /**
   * Check build progress against objectives
   */
  private checkBuildProgress(): void {
    this.objectives.forEach(obj => {
      switch (obj.objective.type) {
        case 'construct_cell':
          // Check if all required components are placed
          if (this.currentBlueprint.requiredComponents) {
            const placedTypes = new Set(this.placedComponents.map(p => p.type));
            const hasAll = this.currentBlueprint.requiredComponents.every(c => placedTypes.has(c));
            obj.current = placedTypes.size;
            obj.target = this.currentBlueprint.requiredComponents.length;
            obj.complete = hasAll;
          }
          break;

        case 'arrange_cells_into_tissue':
          obj.current = this.placedCells.length;
          obj.target = this.currentBlueprint.requiredCellCount || 6;
          obj.complete = obj.current >= obj.target;
          break;

        case 'combine_tissues':
          // Simplified: count placed cells
          obj.current = this.placedCells.length;
          obj.target = this.currentBlueprint.requiredCellCount || 12;
          obj.complete = obj.current >= obj.target;
          break;

        case 'build_functional_organism':
          obj.current = this.placedCells.length;
          obj.target = this.currentBlueprint.requiredCellCount || 20;
          obj.complete = obj.current >= obj.target;
          break;
      }
    });

    this.updateObjectiveDisplay();
  }

  /**
   * Test the construction
   */
  private testConstruction(): void {
    // Validate build
    const results = this.validateBuild();

    // Show results panel
    this.showTestResults(results);

    // If all pass, complete level
    if (results.every(r => r.passed)) {
      this.time.delayedCall(2000, () => {
        this.handleLevelComplete();
      });
    }
  }

  /**
   * Validate the current build against requirements
   */
  private validateBuild(): Array<{ name: string; passed: boolean; feedback: string }> {
    const results: Array<{ name: string; passed: boolean; feedback: string }> = [];

    if (this.buildMode === 'component') {
      // Check component requirements
      if (this.currentBlueprint.requiredComponents) {
        const placedTypes = new Set(this.placedComponents.map(p => p.type));

        this.currentBlueprint.requiredComponents.forEach(comp => {
          const has = placedTypes.has(comp);
          results.push({
            name: COMPONENT_LABELS[comp],
            passed: has,
            feedback: has ? 'Present' : 'Missing',
          });
        });
      }

      // Check life functions
      if (this.currentBlueprint.lifeFunctionsToTest) {
        const hasNucleus = this.placedComponents.some(p => p.type === 'nucleus');
        const hasMitochondria = this.placedComponents.some(p => p.type === 'mitochondria');
        const hasMembrane = this.placedComponents.some(p => p.type === 'membrane');

        if (this.currentBlueprint.lifeFunctionsToTest.includes('metabolism')) {
          const canMetabolize = hasMitochondria;
          results.push({
            name: 'Metabolism',
            passed: canMetabolize,
            feedback: canMetabolize ? 'Cell can produce energy' : 'Needs mitochondria',
          });
        }

        if (this.currentBlueprint.lifeFunctionsToTest.includes('growth')) {
          const canGrow = hasNucleus && hasMembrane;
          results.push({
            name: 'Growth',
            passed: canGrow,
            feedback: canGrow ? 'Cell can grow' : 'Needs nucleus and membrane',
          });
        }
      }
    } else {
      // Check cell count
      if (this.currentBlueprint.requiredCellCount) {
        const hasEnough = this.placedCells.length >= this.currentBlueprint.requiredCellCount;
        results.push({
          name: 'Cell Count',
          passed: hasEnough,
          feedback: hasEnough ? `${this.placedCells.length} cells placed` : `Need ${this.currentBlueprint.requiredCellCount} cells`,
        });
      }

      // Check arrangement
      if (this.currentBlueprint.arrangementPattern) {
        const hasArrangement = this.validateArrangement();
        results.push({
          name: 'Arrangement',
          passed: hasArrangement,
          feedback: hasArrangement ? 'Good arrangement' : 'Cells need better organization',
        });
      }
    }

    return results;
  }

  /**
   * Validate cell arrangement pattern
   */
  private validateArrangement(): boolean {
    // Simplified validation: cells should be somewhat clustered
    if (this.placedCells.length < 2) return false;

    // Check average distance between cells
    let totalDistance = 0;
    let comparisons = 0;

    for (let i = 0; i < this.placedCells.length; i++) {
      for (let j = i + 1; j < this.placedCells.length; j++) {
        const dx = this.placedCells[i].x - this.placedCells[j].x;
        const dy = this.placedCells[i].y - this.placedCells[j].y;
        totalDistance += Math.sqrt(dx * dx + dy * dy);
        comparisons++;
      }
    }

    const avgDistance = totalDistance / comparisons;
    // Cells should be relatively close (within 150px average)
    return avgDistance < 150;
  }

  /**
   * Show test results panel
   */
  private showTestResults(results: Array<{ name: string; passed: boolean; feedback: string }>): void {
    const { width, height } = this.cameras.main;

    // Remove existing panel
    this.testResultsPanel?.destroy();

    this.testResultsPanel = this.add.container(width / 2, height / 2);
    this.testResultsPanel.setDepth(200);

    // Background overlay
    const overlay = this.add.rectangle(0, 0, width, height, 0x000000, 0.7);
    overlay.setInteractive(); // Block clicks
    this.testResultsPanel.add(overlay);

    // Panel background
    const panelBg = this.add.graphics();
    panelBg.fillStyle(0x1a365d, 0.98);
    panelBg.fillRoundedRect(-200, -150, 400, 300, 16);
    panelBg.lineStyle(3, 0x4299e1, 1);
    panelBg.strokeRoundedRect(-200, -150, 400, 300, 16);
    this.testResultsPanel.add(panelBg);

    // Title
    const allPassed = results.every(r => r.passed);
    const title = this.add.text(0, -120, allPassed ? 'TEST PASSED!' : 'TEST RESULTS', {
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: '24px',
      fontStyle: 'bold',
      color: allPassed ? '#48BB78' : '#E2E8F0',
    }).setOrigin(0.5);
    this.testResultsPanel.add(title);

    // Results list
    results.forEach((result, index) => {
      const y = -70 + index * 40;
      const icon = result.passed ? '[PASS]' : '[FAIL]';
      const color = result.passed ? '#48BB78' : '#FC8181';

      const resultText = this.add.text(-180, y, `${icon} ${result.name}`, {
        fontFamily: 'system-ui, -apple-system, sans-serif',
        fontSize: '16px',
        color,
      });
      this.testResultsPanel!.add(resultText);

      const feedbackText = this.add.text(180, y, result.feedback, {
        fontFamily: 'system-ui, -apple-system, sans-serif',
        fontSize: '14px',
        color: '#A0AEC0',
      }).setOrigin(1, 0);
      this.testResultsPanel!.add(feedbackText);
    });

    // Close button
    const closeBtn = this.createButton(0, 110, allPassed ? 'CONTINUE' : 'TRY AGAIN', allPassed ? 0x48bb78 : 0x4299e1, () => {
      this.testResultsPanel?.destroy();
      this.testResultsPanel = undefined;
    });
    this.testResultsPanel.add(closeBtn);

    // Entrance animation
    this.testResultsPanel.setScale(0.8);
    this.testResultsPanel.setAlpha(0);
    this.tweens.add({
      targets: this.testResultsPanel,
      scale: 1,
      alpha: 1,
      duration: 300,
      ease: 'Back.easeOut',
    });
  }

  /**
   * Handle level completion
   */
  private handleLevelComplete(): void {
    // Mark level as complete
    markLevelComplete(this.levelId);

    // Show completion overlay
    this.showCompletionOverlay();
  }

  /**
   * Show level completion overlay
   */
  private showCompletionOverlay(): void {
    const { width, height } = this.cameras.main;

    this.completionOverlay = this.add.container(width / 2, height / 2);
    this.completionOverlay.setDepth(300);

    // Background
    const bg = this.add.rectangle(0, 0, width, height, 0x0a1628, 0.95);
    bg.setInteractive();
    this.completionOverlay.add(bg);

    // Success message
    const title = this.add.text(0, -80, 'LEVEL COMPLETE!', {
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: '48px',
      fontStyle: 'bold',
      color: '#48BB78',
    }).setOrigin(0.5);
    this.completionOverlay.add(title);

    // Stars
    const starsContainer = this.add.container(0, 0);
    for (let i = 0; i < 3; i++) {
      const star = this.add.text(-60 + i * 60, 0, '* ', {
        fontFamily: 'system-ui, -apple-system, sans-serif',
        fontSize: '48px',
        color: '#ECC94B',
      }).setOrigin(0.5);
      starsContainer.add(star);

      // Star animation
      this.tweens.add({
        targets: star,
        scale: { from: 0, to: 1 },
        duration: 400,
        delay: 300 + i * 200,
        ease: 'Back.easeOut',
      });
    }
    this.completionOverlay.add(starsContainer);

    // Level name
    const levelName = this.add.text(0, 60, this.level.name, {
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: '24px',
      color: '#E2E8F0',
    }).setOrigin(0.5);
    this.completionOverlay.add(levelName);

    // Continue button
    const continueBtn = this.createButton(0, 140, 'CONTINUE', 0x4299e1, () => {
      this.scene.start('MenuScene');
    });
    this.completionOverlay.add(continueBtn);

    // Entrance animation
    this.completionOverlay.setAlpha(0);
    this.tweens.add({
      targets: this.completionOverlay,
      alpha: 1,
      duration: 500,
    });
  }
}
